import { IonButton, IonContent, IonHeader, IonInput, IonItem, IonPage, IonText, useIonAlert } from "@ionic/react"
import React, { useContext, useEffect, useState } from "react"
import './Login.css'
import { useForm } from "react-hook-form";
import { Device } from "@capacitor/device";
import { BASE_URL } from "../utils/constant";
import { ActionType, AuthContext } from "../context/AuthContext";
import { Redirect, useHistory } from "react-router-dom";
import { localStore } from "../App";
import InnerMenu from "../components/InnerMenu";

const API_KEY = 'gkzLoFk0MN58TGqCJj';

type CredentialsForm = {
    email: string;
    password: string;
};

type OtpForm = {
    otp: string;
};

const Login: React.FC = () => {
    const credentialsForm = useForm<CredentialsForm>();
    const otpForm = useForm<OtpForm>();
    const [deviceId, setDeviceId] = useState("-");
    const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
    const [loginChallenge, setLoginChallenge] = useState<string | null>(null);
    const [emailMasked, setEmailMasked] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { dispatch, state: { user } } = useContext(AuthContext)
    const history = useHistory();
    const [presentAlert] = useIonAlert();

    const getDeviceInfo = async () => {
        const devId = await Device.getId();
        setDeviceId(devId.identifier);
    };

    useEffect(() => {
        getDeviceInfo();
    }, []);

    const showError = (response: { tit?: string; text?: string; error?: string; message?: string }) => {
        presentAlert({
            header: response.tit || 'Accesso',
            message: response.text || response.error || response.message || "C'era un problema! Per favore riprova.",
            buttons: ['OK'],
        });
    };

    const completeLogin = (response: { user: NonNullable<typeof user> }) => {
        dispatch({ type: ActionType.SET_USER, payload: response.user })
        history.replace('/meatori')
        localStore.set('user', JSON.stringify(response.user));
    };

    const postLoginForm = async (act: string, fields: Record<string, string>) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
        formData.append('act', act);
        formData.append('h', API_KEY);
        formData.append('uuid', deviceId);

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 45000);

        try
        {
            const res = await fetch(`${BASE_URL}/poServer.php`, {
                method: 'post',
                mode: 'cors',
                body: formData,
                headers: {
                    'Accept': 'application/json'
                },
                signal: controller.signal,
            });

            const raw = await res.text();
            let parsed: Record<string, unknown> = {};
            if (raw)
            {
                try
                {
                    parsed = JSON.parse(raw);
                }
                catch
                {
                    throw new Error('Risposta non valida dal server. Riprovare più tardi.');
                }
            }

            if (!res.ok && !parsed.error && !parsed.tit)
            {
                parsed.error = `Errore di rete (${res.status}). Riprovare.`;
            }

            return parsed as {
                user?: NonNullable<typeof user>;
                requires_2fa?: boolean;
                login_challenge?: string;
                email_masked?: string;
                code?: number;
                error?: string;
                message?: string;
                tit?: string;
                text?: string;
            };
        }
        finally
        {
            window.clearTimeout(timeoutId);
        }
    };

    const handleRequestError = (error: unknown) => {
        console.log(error);
        const message = error instanceof DOMException && error.name === 'AbortError'
            ? 'Il server impiega troppo tempo a rispondere. Verifica la connessione e riprova.'
            : error instanceof Error
                ? error.message
                : "C'era un problema! Per favore riprova.";
        presentAlert({
            header: 'Accesso',
            message,
            buttons: ['OK'],
        });
    };

    const login = async (data: CredentialsForm) => {
        setSubmitting(true);
        try
        {
            const response = await postLoginForm('10', {
                username: data.email,
                password: data.password,
            });

            if (response.user)
            {
                completeLogin({ user: response.user });
                return;
            }

            if (response.requires_2fa && response.login_challenge)
            {
                setLoginChallenge(response.login_challenge);
                setEmailMasked(response.email_masked || '');
                setStep('otp');
                otpForm.reset({ otp: '' });
                return;
            }

            showError(response);
        }
        catch (error)
        {
            handleRequestError(error);
        }
        finally
        {
            setSubmitting(false);
        }
    };

    const verifyOtp = async (data: OtpForm) => {
        if (!loginChallenge)
        {
            setStep('credentials');
            return;
        }

        setSubmitting(true);
        try
        {
            const response = await postLoginForm('21', {
                login_challenge: loginChallenge,
                otp: data.otp.replace(/\s/g, ''),
            });

            if (response.user)
            {
                completeLogin({ user: response.user });
                return;
            }

            showError(response);
            if (response.code === 410 || response.code === 404 || response.code === 429)
            {
                setStep('credentials');
                setLoginChallenge(null);
            }
        }
        catch (error)
        {
            handleRequestError(error);
        }
        finally
        {
            setSubmitting(false);
        }
    };

    const resendOtp = async () => {
        if (!loginChallenge)
        {
            return;
        }

        setSubmitting(true);
        try
        {
            const response = await postLoginForm('22', {
                login_challenge: loginChallenge,
            });

            if (response.code === 200)
            {
                if (response.email_masked)
                {
                    setEmailMasked(response.email_masked);
                }
                presentAlert({
                    header: 'Codice inviato',
                    message: response.message || 'Ti abbiamo inviato un nuovo codice via email.',
                    buttons: ['OK'],
                });
                return;
            }

            showError(response);
        }
        catch (error)
        {
            handleRequestError(error);
        }
        finally
        {
            setSubmitting(false);
        }
    };

    const backToCredentials = () => {
        setStep('credentials');
        setLoginChallenge(null);
        otpForm.reset({ otp: '' });
    };

    if (user?.token)
        return <Redirect to={'/meatori'} />

    return (
        <IonPage >
            <IonHeader>
                <InnerMenu name={'abc'} />
            </IonHeader>
            <IonContent fullscreen className="page-login">
                {step === 'credentials' && (
                    <form className="form-login" onSubmit={credentialsForm.handleSubmit(login)}>
                        <IonItem>
                            <IonInput label="Nome utente" {...credentialsForm.register("email", { required: true, minLength: 2 })} />
                            {credentialsForm.formState.errors.email?.type === 'required' &&
                                <IonText color="danger" className="ion-padding-start">
                                    <small>Inserisci il tuo nome utente</small>
                                </IonText>
                            }
                        </IonItem>

                        <IonItem>
                            <IonInput label="Password" type="password" {...credentialsForm.register("password", { required: { value: true, message: 'Inserire la password' } })} />
                            {credentialsForm.formState.errors.password &&
                                <IonText color="danger" className="ion-padding-start">
                                    <small>{credentialsForm.formState.errors.password.message}</small>
                                </IonText>
                            }
                        </IonItem>

                        <IonButton className="ion-margin-top feedSubBtn" type="submit" expand="block" disabled={submitting}>
                            Accedi
                        </IonButton>
                    </form>
                )}

                {step === 'otp' && (
                    <form className="form-login" onSubmit={otpForm.handleSubmit(verifyOtp)}>
                        <IonText className="ion-padding">
                            <p>Inserisci il codice a 6 cifre inviato a {emailMasked || 'la tua email'}.</p>
                        </IonText>
                        <IonItem>
                            <IonInput
                                label="Codice di verifica"
                                inputMode="numeric"
                                maxlength={6}
                                {...otpForm.register("otp", {
                                    required: { value: true, message: 'Inserire il codice' },
                                    pattern: { value: /^\d{6}$/, message: 'Il codice deve essere di 6 cifre' },
                                })}
                            />
                            {otpForm.formState.errors.otp &&
                                <IonText color="danger" className="ion-padding-start">
                                    <small>{otpForm.formState.errors.otp.message}</small>
                                </IonText>
                            }
                        </IonItem>

                        <IonButton className="ion-margin-top feedSubBtn" type="submit" expand="block" disabled={submitting}>
                            Verifica codice
                        </IonButton>
                        <IonButton className="ion-margin-top" type="button" expand="block" fill="outline" disabled={submitting} onClick={resendOtp}>
                            Invia di nuovo il codice
                        </IonButton>
                        <IonButton className="ion-margin-top" type="button" expand="block" fill="clear" disabled={submitting} onClick={backToCredentials}>
                            Torna al login
                        </IonButton>
                    </form>
                )}
            </IonContent>
        </IonPage>
    )
}
export default Login
